'use client';

import React, { useState, useRef, useCallback } from 'react';
import { Send, Mic, MicOff, Paperclip, Square, X, FileText, Image } from 'lucide-react';

interface Props {
  onSend: (content: string, file?: File) => void;
  isTyping: boolean;
  onStop?: () => void;
  keyboardOpen?: boolean;
  isGuest?: boolean;
}

function MessageComposer({ onSend, isTyping, onStop, keyboardOpen = false, isGuest = false }: Props) {
  const [value, setValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isSendingRef = useRef(false);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    autoResize();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
    ];
    if (!validTypes.includes(file.type)) {
      alert('Supported file types: JPEG, PNG, GIF, WebP, PDF');
      return;
    }
    setAttachedFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = () => {
    setAttachedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if ((!trimmed && !attachedFile) || isTyping || isSendingRef.current) return;

    isSendingRef.current = true;
    onSend(trimmed || (attachedFile ? `[Attached: ${attachedFile.name}]` : ''), attachedFile ?? undefined);
    setValue('');
    setAttachedFile(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    setTimeout(() => {
      textareaRef.current?.focus();
      isSendingRef.current = false;
    }, 50);
  }, [value, attachedFile, isTyping, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const bottomPadding = keyboardOpen
    ? 'env(safe-area-inset-bottom, 0px)'
    : isGuest
    ? 'env(safe-area-inset-bottom, 0px)'
    : 'calc(env(safe-area-inset-bottom, 0px) + 64px)';

  const canSend = (value.trim().length > 0 || attachedFile !== null) && !isTyping;
  const isImage = attachedFile?.type.startsWith('image/');

  return (
    <div
      className="px-3 pt-2 pb-2 bg-background/95 backdrop-blur-md border-t border-border/50"
      style={{ paddingBottom: bottomPadding }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Attachment preview */}
        {attachedFile && (
          <div className="flex items-center gap-2 mb-2 px-1">
            <div className="flex items-center gap-2 bg-primary/8 border border-primary/15 rounded-2xl px-3 py-1.5 text-sm text-primary max-w-full overflow-hidden">
              {isImage ? <Image size={13} className="flex-shrink-0" /> : <FileText size={13} className="flex-shrink-0" />}
              <span className="truncate max-w-[200px] text-[13px] font-medium">{attachedFile.name}</span>
              <button
                type="button"
                onClick={removeAttachment}
                className="flex-shrink-0 ml-1 hover:text-destructive transition-colors"
                aria-label="Remove attachment"
              >
                <X size={12} />
              </button>
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
          onChange={handleFileChange}
          className="hidden"
          aria-hidden="true"
        />

        {/* Pill composer */}
        <div className="flex items-end gap-2 bg-card border border-border/70 rounded-[28px] px-3 py-2.5 shadow-sm transition-all duration-200 focus-within:border-primary/40 focus-within:shadow-md">
          {/* Attachment button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 active:scale-90 mb-0.5 ${
              attachedFile
                ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-primary hover:bg-primary/8'
            }`}
            title="Attach file"
            aria-label="Attach file"
          >
            <Paperclip size={16} />
          </button>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            rows={1}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Tell Clingy what's on your mind…"
            disabled={isTyping}
            style={{ fontSize: '16px', resize: 'none', lineHeight: '1.5' }}
            className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground/70 outline-none transition-all duration-200 disabled:opacity-50 overflow-hidden py-0.5 font-normal"
          />

          {/* Voice button */}
          <button
            type="button"
            onClick={() => setIsRecording((v) => !v)}
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 active:scale-90 mb-0.5 ${
              isRecording
                ? 'bg-primary text-white pulse-soft' :'text-muted-foreground hover:text-primary hover:bg-primary/8'
            }`}
            title={isRecording ? 'Stop recording' : 'Voice input'}
            aria-label={isRecording ? 'Stop recording' : 'Voice input'}
          >
            {isRecording ? <MicOff size={15} /> : <Mic size={15} />}
          </button>

          {/* Send / Stop button */}
          {isTyping ? (
            <button
              type="button"
              onClick={onStop}
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-muted text-muted-foreground hover:bg-muted/80 transition-all duration-200 active:scale-90 mb-0.5"
              aria-label="Stop generating"
              title="Stop generating"
            >
              <Square size={13} className="fill-current" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 active:scale-90 mb-0.5 ${
                canSend
                  ? 'lavender-gradient text-white shadow-sm hover:opacity-90'
                  : 'bg-muted text-muted-foreground opacity-40'
              }`}
              aria-label="Send message"
            >
              <Send size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default MessageComposer;